// Pure rules of a signature image upload (node-tested in ci/document-review-editors.test.cjs). The
// upload is decoded and redrawn on a canvas the size of the signature pad, so whatever the file
// (SVG, a multi-MB photo…) the stored answer is the pad's own shape: a PNG data URL the PDF prints
// as an image (mfDocumentAdapter isImageString).

/** The signature pad's size (servicePlan/components/SignatureCanvas.tsx). */
export const SIGNATURE_IMAGE_WIDTH = 520;
export const SIGNATURE_IMAGE_HEIGHT = 140;

export const MAX_SIGNATURE_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * Why a picked file is refused before decoding it; null = try to decode it. A file with no MIME
 * type (some OSes leave it empty) is tried: it is accepted only if the browser can decode it.
 */
export function signatureUploadError(file: { size: number; type: string }): string | null {
  if (file.type !== "" && !file.type.startsWith("image/")) {
    return "Choose an image file.";
  }
  if (file.size > MAX_SIGNATURE_UPLOAD_BYTES) {
    return "The image is larger than 5 MB. Choose a smaller file.";
  }
  return null;
}

export interface Rect {
  height: number;
  width: number;
  x: number;
  y: number;
}

/**
 * Where an image of `source` size is drawn to fit inside `target` without distortion, centred.
 * An image without an intrinsic size (e.g. an SVG with no width / height) fills the target.
 */
export function aspectFitRect(
  source: { height: number; width: number },
  target: { height: number; width: number }
): Rect {
  if (!(source.width > 0) || !(source.height > 0)) {
    return { x: 0, y: 0, width: target.width, height: target.height };
  }
  const scale = Math.min(target.width / source.width, target.height / source.height);
  const width = source.width * scale;
  const height = source.height * scale;
  return {
    x: (target.width - width) / 2,
    y: (target.height - height) / 2,
    width,
    height,
  };
}
